<template>
    <section id="Clinician__Tasks" class="dashboard-card d-flex">
        <CardTemplate :borderClass="`task-section ${isTaskList ? '' : 'py-2'}`">
            <!-- HEADER -->
            <template v-slot:header>
                <section class="d-flex flex-column card-title w-100 pe-2 pb-0">
                    <div
                        class="d-flex align-items-center justify-content-between fw-bold font-size-md w-100 position-relative"
                    >
                        <div :class="`${isTaskList ? 'task-list-full' : 'task-list-empty'}`">
                            <span> Visits </span>
                            <span v-if="taskList.length"> ({{ taskList.length }}) </span>
                            <a
                                tabindex="0"
                                href="javascript:void(0);"
                                role="button"
                                v-custom-popover="{ content: tooltip, className: 'w-30 font-size-sm info-popup' }"
                            >
                                <i class="fas fa-info-circle text-primary ms-1"></i>
                            </a>
                        </div>
                        <div class="d-flex align-items-center justify-content-start date-toggle-container">
                            <i
                                :class="
                                    'fas fa-chevron-left cursor-pointer' +
                                    `${decrementDisabled ? 'no-navigation' : ''} ${isLoading ? 'disabled' : ''}`
                                "
                                @click="decrementTaskDate()"
                                v-tooltip="{ title: bwdNavTooltip }"
                            ></i>
                            <span v-if="!isLoading && myTasksDate" class="mx-2 font-size-sm fw-bold">
                                {{ formatDate(myTasksDate.toString(), "ddd, DD MMM , YYYY") }}
                            </span>
                            <span v-if="isLoading" class="mx-2 font-size-sm fw-bold">
                                <i class="fas fa-spinner fa-pulse fa-fw"></i>
                            </span>
                            <span
                                class="calendar-icon d-flex justify-content-center align-items-center"
                                @click="toggleCalendar"
                            >
                                <i :class="`fas fa-calendar-alt ${showCalendar == true ? 'active' : ''}`"></i>
                            </span>
                            <i
                                :class="
                                    'fas fa-chevron-right cursor-pointer ps-1' +
                                    ` ${incrementDisabled ? 'no-navigation' : ''} ${isLoading ? 'disabled' : ''}`
                                "
                                @click="incrementTaskDate()"
                                v-tooltip="{ title: fwdNavTooltip }"
                            ></i>
                        </div>
                        <div
                            class="text-center text-lg-end"
                            @mouseover="showLogo(isAnyPremiumAgency ? true : false)"
                            @mouseout="showLogo(false)"
                        >
                            <div
                                @click="routeVisits"
                                v-if="isAnyPremiumAgency && isTaskList"
                                v-tooltip="{ title: routeVisitTooltip, placement: 'right' }"
                                class="ai-border"
                            >
                                <span> Route Visits </span>
                            </div>
                            <div
                                v-if="!isAnyPremiumAgency && isTaskList"
                                class="ai-border ai-disabled"
                                v-tooltip="{
                                    title: `Contact Axxess to unlock this <br /> premium Axxess intelligence feature.`,
                                    placement: 'top',
                                    className: 'lock-premium'
                                }"
                            >
                                <span> Route Visits </span>
                                <img src="../../../../assets/images/lock.svg" />
                            </div>
                        </div>
                    </div>
                </section>
            </template>

            <!-- CONTENT -->

            <template #content>
                <section
                    ref="tasksContainerRef"
                    class="w-100 h-100 overflow-auto pb-2"
                    :class="`${userOs == 'Windows' ? 'custom-scrollbar' : ''}`"
                >
                    <div class="calendar-no-visits-vue text-dark mt-2" v-show="showCalendar">
                        <div ref="noVisitDatePicker" class="schedule-calendar"></div>
                    </div>
                    <TasksMap
                        v-if="taskList.length > 0"
                        :my-task-date="myTasksDate"
                        :task-list="taskList"
                        :selected-task="selectedTask"
                        v-on:selected-task-changed="selectedTaskChanged"
                        :list-update="listUpdate"
                    />
                    <div v-else class="img-map-container">
                        <img class="me-1" src="../../../../assets/images/default-map-usa.png" />
                    </div>

                    <!-- LOADING -->

                    <div class="mt-3 text-center" v-show="isLoading && !hasError">
                        <i class="fas fa-spinner fa-pulse fa-fw"></i>
                        <b>Loading your Visits</b>
                        <i class="fas fa-spinner fa-pulse fa-fw"></i>
                    </div>

                    <!-- ERROR -->

                    <div class="mt-5 text-center" v-show="hasError && !isLoading">
                        <p>
                            There was a problem while fetching your Visits Schedule.
                            <br />
                            Please try again.
                        </p>
                        <button class="btn btn-primary" @click="getTasks()">Try Again</button>
                    </div>

                    <!-- NO DATA -->

                    <div v-if="taskList.length === 0 && !isLoading && !hasError" class="no-data">
                        <i class="mb-3 text-center text-gray mt-n2">
                            You have no visits scheduled {{ isPresent ? "today." : "on" }}
                            <b v-if="!isPresent">
                                {{ formatDate(myTasksDate.toString(), "MMMM DD, YYYY") }}
                            </b>
                        </i>
                    </div>

                    <!-- DATA -->

                    <div v-if="!isLoading && !hasError && taskList.length > 0">
                        <draggable
                            :list="taskList"
                            handle=".dragger"
                            ghostClass="ghost"
                            @start="dragging = true"
                            @end="dragging = false"
                            @change="orderChanged(taskList)"
                            item-key="id"
                        >
                            <template #item="{ element: task }">
                                <div class="router-task position-relative">
                                    <div
                                        class="d-flex flex-column align-items-center justify-content-center me-3 text-danger fw-bold"
                                    >
                                        <p>{{ task.key }}</p>
                                    </div>

                                    <section class="d-flex flex-column flex-grow-1">
                                        <div class="d-flex align-items-center justify-content-between">
                                            <div>
                                                <span class="font-size-xs mb-1 fw-bold">
                                                    {{ task.VisitName }}
                                                </span>
                                            </div>
                                            <div>
                                                <span v-if="task.OnCall" class="pill-oncall pill me-2"> On-Call </span>
                                                <span v-if="task.PRN" class="pill-prn pill me-2"> PRN </span>
                                                <span v-if="task.VisitConfirmed" class="me-2">
                                                    <i class="fas fa-check-circle confirmed-check me-1"></i>
                                                    <span class="confirmed-text"><strong>Confirmed</strong></span>
                                                </span>
                                                <!-- <span v-if="task.PatientCalled" class="me-2">
                                                    <i class="fas fa-exclamation-circle call-office-status me-1"></i>
                                                    <span class="call-office-status"
                                                        ><strong>Call the Office</strong></span
                                                    >
                                                </span> -->
                                                <!-- commented code will need in future -->
                                                <span v-if="task.PatientCalled" class="me-2">
                                                    <i class="fas fa-exclamation-circle reschedule-status me-1"></i>
                                                    <span class="reschedule-status"><strong>Reschedule</strong></span>
                                                </span>
                                                <!-- using 'CalledPatient' temporary until we get veriable for "Call To Reschedule" from Solution  -->
                                                <span class="font-size-xs">
                                                    {{
                                                        formatVisitTime(
                                                            new Date(task.StartDateTime),
                                                            new Date(task.EndDateTime)
                                                        )
                                                    }}
                                                </span>
                                            </div>
                                        </div>
                                        <div class="d-flex align-items-center fw-bold">
                                            <a href="javascript:void(0);" @click="viewPatient(task)" class="me-1">
                                                {{ task.patientFullName }}
                                            </a>
                                            <i
                                                v-if="task.isPatientBirthDate"
                                                class="fas fa-birthday-cake"
                                                v-tooltip="{ title: task.birthDateTooltip }"
                                            ></i>
                                            <div class="position-absolute top-3 dragger">
                                                <span class="drag-icon">
                                                    <i class="fas fa-exchange-alt font-size-3xs"></i>
                                                </span>
                                            </div>
                                        </div>
                                        <div class="font-size-xs">
                                            <span
                                                v-if="
                                                    (task.Application === ApplicationEnum.AxxessHospiceFE ||
                                                        task.Application === ApplicationEnum.AxxessPalliative) &&
                                                    task.IsMissedVisit
                                                "
                                            >
                                                Missed Visit ({{ task.StatusName }})
                                            </span>
                                            <span v-else>
                                                {{ task.StatusName }}
                                            </span>
                                        </div>
                                        <div class="font-size-xs my-1">
                                            <span>
                                                {{ task.fullAddress }} {{ task.PatientPhoneNumber ? "-" : "" }}
                                                <a
                                                    v-if="task.PatientPhoneNumber"
                                                    :href="'tel:' + task.PatientPhoneNumber"
                                                    class="mb-0 font-size-xs w-auto"
                                                    >{{ formatPhone(task.PatientPhoneNumber) }}</a
                                                ></span
                                            >
                                        </div>
                                        <div class="d-flex justify-content-between align-items-center">
                                            <div class="d-flex align-items-center">
                                                <ProductPill
                                                    class="align-self-start"
                                                    :product-id="getApplicationId(task.Application)"
                                                />
                                                <div
                                                    class="text-default ms-2 font-size-2xs"
                                                    v-tooltip="{ title: task.agencyTooltip, placement: 'top' }"
                                                >
                                                    {{ task.BranchName }}
                                                </div>
                                            </div>
                                            <div class="right-section d-flex align-items-center">
                                                <i
                                                    class="fas fa-sticky-note orange-note me-2"
                                                    v-tooltip="{
                                                        title: task.ReturnComments,
                                                        placement: 'right',
                                                        className: 'orange-note'
                                                    }"
                                                    v-if="isOrange(task)"
                                                ></i>
                                                <i
                                                    class="me-2 fas fa-sticky-note red-note"
                                                    v-tooltip="{
                                                        title: redToolTip(task),
                                                        placement: 'right',
                                                        className: 'red-note'
                                                    }"
                                                    v-if="isRed(task) && task.Status !== '405'"
                                                ></i>
                                                <i
                                                    class="me-2 fas fa-sticky-note blue-note"
                                                    v-tooltip="{
                                                        title: task.EpisodeNotes,
                                                        placement: 'right',
                                                        className: 'blue-note'
                                                    }"
                                                    v-if="task.EpisodeNotes?.length"
                                                ></i>
                                                <i
                                                    class="me-2 fas fa-sticky-note yellow-note"
                                                    v-tooltip="{
                                                        title: task.Comments,
                                                        placement: 'right',
                                                        className: 'yellow-note'
                                                    }"
                                                    v-if="showComments(task)"
                                                ></i>

                                                <button
                                                    href="javascript:void(0);"
                                                    @click="startTask(task)"
                                                    :class="`btn btn-primary ${task.statusNameType}`"
                                                    v-if="
                                                        task.statusNameType &&
                                                        !isMissedVisit(task.Status) &&
                                                        isHomeCare(task) &&
                                                        isFutureDocumentationPreventedHC(task) &&
                                                        checkPermission(
                                                            task.statusNameType,
                                                            task.Application,
                                                            task.AgencyId
                                                        ) &&
                                                        !isHosOrPal(task.Application)
                                                    "
                                                    v-tooltip="{ title: task.buttonTooltip }"
                                                >
                                                    {{ task.statusNameType }}
                                                </button>
                                                <button
                                                    href="javascript:void(0);"
                                                    @click="startTask(task)"
                                                    :class="`btn btn-primary ${task.statusNameType}`"
                                                    v-else-if="
                                                        task.statusNameType &&
                                                        !isMissedVisit(task.Status) &&
                                                        !isFuture() &&
                                                        checkPermission(
                                                            task.statusNameType,
                                                            task.Application,
                                                            task.AgencyId
                                                        ) &&
                                                        !isHosOrPal(task.Application)
                                                    "
                                                    v-tooltip="{ title: task.buttonTooltip }"
                                                >
                                                    {{ task.statusNameType }}
                                                </button>
                                                <button
                                                    href="javascript:void(0);"
                                                    @click="startHosPalTask(task)"
                                                    :class="`btn btn-primary isPendingApproval ${
                                                        task.statusNameHosPal == 'Pending Approval' ? 'disabled' : ''
                                                    }`"
                                                    v-if="
                                                        task.statusNameHosPal &&
                                                        showFutureVisit(task.IsFutureDocumentationPrevented) &&
                                                        !(task.Application === ApplicationEnum.AxxessPalliative
                                                            ? task.IsMissed
                                                            : task.IsMissedVisit) &&
                                                        checkPermission(
                                                            task.statusNameHosPal,
                                                            task.Application,
                                                            task.AgencyId
                                                        ) &&
                                                        isHosOrPal(task.Application)
                                                    "
                                                    v-tooltip="{ title: task.buttonTooltip }"
                                                >
                                                    <span>{{ task.statusNameHosPal }}</span>
                                                </button>
                                                <span class="dropdown app-menu ms-2">
                                                    <a
                                                        class="text-dark fw-bold font-size-sm"
                                                        href="javascript:void(0);"
                                                        id="User_Dropdown"
                                                        role="button"
                                                        aria-haspopup="true"
                                                        aria-expanded="false"
                                                    >
                                                        <i class="fas fa-ellipsis-h ellipsis"></i>
                                                    </a>
                                                    <div
                                                        class="dropdown-menu dropdown-menu-end mt-0 font-size-xs text-center"
                                                        aria-labelledby="User_Dropdown"
                                                    >
                                                        <a href="javascript:void(0);" @click="openVisitDetails(task)">
                                                            <span>Details</span>
                                                        </a>
                                                    </div>
                                                </span>
                                            </div>
                                        </div>
                                    </section>
                                </div>
                            </template>
                        </draggable>
                    </div>
                </section>
            </template>
        </CardTemplate>
    </section>
</template>

<script lang="ts" src="./TaskList.ts"></script>
<style src="./TaskList.scss"></style>
